import { Map, OrderedMap } from 'immutable';
import { pipe, Option, Array as A } from 'effect';

import type { MatchedLineResult, MatchedLines } from '@/domain/journal/schemas';

/** Column letter -> the journal-entry field rendered in it, as `jeGridLineColumnsAdapter` builds it. */
type GridLineColumns = OrderedMap<string, string>;

/** Grid address (e.g. `C5`) -> the document's value for that cell. */
export type MismatchedCells = Map<string, string>;

type PartialMatch = Extract<MatchedLineResult, { status: 'partial_match' }>;

const isPartialMatch = (result: MatchedLineResult): result is PartialMatch => result.status === 'partial_match';

/**
 * Translate the match payload's coordinates into the grid's own.
 *
 * The payload speaks field names and journal-entry line numbers; the grid speaks cell
 * addresses. The payload's outer key is the JOURNAL-ENTRY line, while the `line` inside a
 * result is the row of the DOCUMENT's recognised table and is of no use here.
 *
 * Only the first result of a line is read, so the cell-level highlight can never disagree
 * with the line-level one, which reads the same result.
 */
export function mismatchedCellsAdapter(
  matchedLines: MatchedLines,
  columnsDictionary: GridLineColumns,
  headerLinesNumber: number,
): MismatchedCells {
  const columnByField = columnsDictionary.flip();

  return pipe(
    Object.entries(matchedLines),
    A.flatMap(([jeLine, results]) =>
      pipe(
        Option.fromNullable(results[0]),
        Option.filter(isPartialMatch),
        Option.map((result) =>
          pipe(
            Object.entries(result.cells),
            // A field with no column of its own cannot be highlighted, so it is dropped.
            A.filterMap(([field, cell]) =>
              pipe(
                Option.fromNullable(columnByField.get(field)),
                Option.map(
                  (col) => [`${col}${Number(jeLine) + headerLinesNumber}`, cell.doc_value] as [string, string],
                ),
              ),
            ),
          ),
        ),
        Option.getOrElse((): [string, string][] => []),
      ),
    ),
    (addresses) => Map<string, string>(addresses),
  );
}
